// Reschedule appointment picker — shown when the Step-1 lookup matches more
// than one upcoming appointment for the same name + email. One card per
// appointment; selecting one hydrates the booking flow from it (widget.tsx's
// hydrateFromAppointment). Back returns to the reschedule lead form.
//
// Styling borrows the CancelLookup wrapper + the location-dropdown row
// treatment, per the widget's CSS-module conventions.

import styles from '../styles/widget.module.css';
import { formatLongDate } from '../utils/datetime';
import { countryConfigOrDefault } from '../config/countries';
import { storeDisplayName } from '../data/storeDisplayNames';
import type { LookedUpAppointment } from '../types';

interface RescheduleAppointmentPickerProps {
  candidates: LookedUpAppointment[];
  /** Widget country scope — drives date formatting + store display names. */
  country: string | null;
  onSelect: (appointment: LookedUpAppointment) => void;
  onBack: () => void;
}

/**
 * The date column: prefer the structured YYYY-MM-DD through the shared
 * long-date formatter; anything else (defensive — the middleware contract
 * says YYYY-MM-DD) renders as-is rather than as "Invalid Date".
 */
function displayDate(date: string, locale: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? formatLongDate(date, locale) : date;
}

/**
 * Time portion of SF's tz-naive display text — "02/26/2026 9:00 AM" → "9:00 AM".
 * Pure string manipulation: startsAtText is store wall-clock and must never
 * round-trip through Date (fake-Z convention, see utils/datetime.ts).
 */
function displayTime(startsAtText: string): string {
  return startsAtText.trim().replace(/^\S+\s*/, '');
}

export function RescheduleAppointmentPicker({
  candidates,
  country,
  onSelect,
  onBack,
}: RescheduleAppointmentPickerProps) {
  const { locale } = countryConfigOrDefault(country);

  return (
    <div className={styles.cancelBookingWrapper}>
      <h2 className={styles.heading}>Which appointment?</h2>
      <p className={styles.subtitle}>
        We found more than one upcoming appointment for you. Choose the one
        you’d like to move.
      </p>

      <div className={styles.reschedulePickerList}>
        {candidates.map((appt) => (
          <div key={appt.appointmentNumber} className={styles.reschedulePickerCard}>
            <div>
              <div className={styles.locationName}>
                {displayDate(appt.date, locale)}
                {displayTime(appt.startsAtText) ? ` · ${displayTime(appt.startsAtText)}` : ''}
              </div>
              <div className={styles.reschedulePickerMeta}>
                {storeDisplayName(appt.storeName, country)}
              </div>
              {appt.serviceNames.length > 0 && (
                <div className={styles.reschedulePickerMeta}>
                  {appt.serviceNames.join(', ')}
                </div>
              )}
            </div>
            <button
              type="button"
              className={`${styles.button} ${styles.reschedulePickerSelect}`}
              data-cc-track="cta"
              data-cc-track-id="reschedule_pick_appointment"
              onClick={() => onSelect(appt)}
            >
              Select
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className={styles.linkButton}
        onClick={onBack}
        style={{ marginTop: 12, display: 'block', width: '100%', textAlign: 'center' }}
      >
        Back
      </button>
    </div>
  );
}
